import * as React from "react";
import { FlightSubTextProps, LoBRowProps, PackageInfoProps } from "components/flexComponents/Packages/typings";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkIcon, UitkIconSize } from "uitk-react-icons";
import { UitkText, UitkHeading } from "uitk-react-text";
import { getFormattedHotelDates } from "components/flexComponents/Packages/utils/PackageDates";
import { useLocalization } from "@shared-ui/localization-context";

const LobDetails: React.FC<PackageInfoProps> = ({ packageItem }) => {
  const {
    destinationCityName,
    destinationAirport,
    originCityName,
    originAirport,
    isoCheckInDate,
    isoCheckOutDate,
    numberOfNights,
    nonStop,
    airCabinCode,
  } = packageItem;
  const { formatText } = useLocalization();

  const originDestination = "package.card.flight.tripOriginDestination";

  const shouldDisplayHotelText = isoCheckInDate && isoCheckOutDate && numberOfNights;
  const shouldDisplayFlightText = typeof nonStop !== "undefined" && airCabinCode;
  if (!shouldDisplayHotelText && !shouldDisplayFlightText) {
    return null;
  }

  const returnAirlineDifferent =
    packageItem.carrierCodePerLeg &&
    packageItem.carrierCodePerLeg.length > 1 &&
    packageItem.carrierCodePerLeg[0] !== packageItem.carrierCodePerLeg[1] &&
    packageItem.carrierNamePerLeg !== undefined;

  return (
    <UitkLayoutFlex direction="column" className="m-t-margin-two l-t-margin-two xl-t-margin-two">
      {shouldDisplayHotelText && (
        <LobRow
          icon="lob_hotels"
          text={getFormattedHotelDates({
            checkInDate: isoCheckInDate,
            checkOutDate: isoCheckOutDate,
          })}
          subText={
            numberOfNights > 1
              ? formatText("package.card.hotelNightStay.single", numberOfNights)
              : formatText("package.card.hotelNightStay.multiple", numberOfNights)
          }
        />
      )}
      {shouldDisplayFlightText && (
        <LobRow
          icon="lob_flights"
          text={formatText(
            originDestination,
            ...[originCityName, originAirport, destinationCityName, destinationAirport]
          )}
          subText={getFlightSubText({
            airline:
              (packageItem.carrierNamePerLeg && packageItem.carrierNamePerLeg[0]) ??
              (packageItem.carrierCodePerLeg && packageItem.carrierCodePerLeg[0]),
            isNonstop: nonStop,
            cabinClass: airCabinCode,
            returnAirlineDifferent,
          })}
        />
      )}
      {shouldDisplayFlightText && returnAirlineDifferent && (
        <LobRow
          icon="lob_flights"
          text={formatText(
            originDestination,
            ...[destinationCityName, destinationAirport, originCityName, originAirport]
          )}
          subText={getFlightSubText({
            airline: packageItem.carrierNamePerLeg[1] ?? packageItem.carrierCodePerLeg[1],
            isNonstop: nonStop,
            cabinClass: airCabinCode,
            returnAirlineDifferent,
          })}
        />
      )}
    </UitkLayoutFlex>
  );
};
export default LobDetails;

const LobRow: React.FC<LoBRowProps> = ({ icon, text, subText }: LoBRowProps) => (
  <UitkLayoutFlexItem className="all-y-margin-one">
    <UitkLayoutFlex>
      <UitkLayoutFlexItem>
        <UitkIcon name={icon} size={UitkIconSize.SMALL} className="all-r-margin-two" />
      </UitkLayoutFlexItem>
      <UitkLayoutFlex direction="column">
        <UitkHeading level={4} size={300}>
          {text}
        </UitkHeading>
        <UitkText size={200}>{subText}</UitkText>
      </UitkLayoutFlex>
    </UitkLayoutFlex>
  </UitkLayoutFlexItem>
);

const getFlightSubText = ({ airline, isNonstop, cabinClass, returnAirlineDifferent }: FlightSubTextProps) => {
  const { formatText } = useLocalization();
  const airlineText = airline ? `${airline} • ` : "";
  const tripText = returnAirlineDifferent
    ? isNonstop
      ? `${formatText("package.card.flight.nonstop")} • `
      : ""
    : isNonstop
    ? `${formatText("package.card.flight.nonstopRoundtrip")} • `
    : `${formatText("package.card.flight.roundtrip")} • `;

  return `${airlineText}${tripText}${cabinClass}`;
};
